import { useMutation } from "@apollo/client";
import { LoadingButton } from "@mui/lab";
import { Box, Stack, TextField } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { gql } from "generated-graphql";
import { useState } from "react";

import { DrawerSectionHeading, drawerSectionSx } from "./DrawerSection";
import { EXCEEDANCE_REFETCH, ExceedanceKey } from "./exceedanceKeys";

const SET_SLA_MISS_REASON = gql(`
  mutation SetGeneratorStatusExceedanceSlaMissReason(
    $exceedance: GeneratorStatusExceedanceKeyInput!
    $reason: String
  ) {
    setGeneratorStatusExceedanceSlaMissReason(exceedance: $exceedance, reason: $reason) {
      id
      slaMissReason
    }
  }
`);

type Props = {
  exceedanceKey: ExceedanceKey;
  savedReason: string | null;
};

/** Staff's reason for missing the customer's SLA. Key it on the saved reason to reset the draft. */
export function SlaMissReason({ exceedanceKey, savedReason }: Props) {
  const alerts = useAlerts();
  const [draft, setDraft] = useState(savedReason ?? "");
  const [save, { loading }] = useMutation(SET_SLA_MISS_REASON, {
    refetchQueries: EXCEEDANCE_REFETCH,
    awaitRefetchQueries: true,
  });

  const runSave = async () => {
    try {
      await save({ variables: { exceedance: exceedanceKey, reason: draft } });
      alerts.success("SLA miss reason saved.");
    } catch (e) {
      alerts.error("Failed to save the SLA miss reason.", e);
    }
  };

  return (
    <Box sx={drawerSectionSx}>
      <DrawerSectionHeading>Reason for SLA miss</DrawerSectionHeading>
      <Stack spacing={1} alignItems="flex-end">
        <TextField
          fullWidth
          multiline
          minRows={2}
          size="small"
          placeholder="Why this missed the customer's SLA. Included in exports."
          inputProps={{ "aria-label": "Reason for SLA miss" }}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
        />
        <LoadingButton
          size="small"
          variant="outlined"
          loading={loading}
          disabled={draft.trim() === (savedReason ?? "")}
          onClick={runSave}
        >
          Save reason
        </LoadingButton>
      </Stack>
    </Box>
  );
}
